// apps/client/src/pages/leads/LeadSlaDashboardPage.tsx
//
// Phase E observability — Lead SLA dashboard. Surfaces three signals:
//   - Unanswered leads in last 24h (call-to-action for admin)
//   - Avg SLA breach time (mins) across the lookback window
//   - Top 3 brokers ranked by breach count
//
// Data source: `getLeadSlaSummary` callable (Firestore aggregation over
// companies/{c}/leads). No BigQuery touch — keeps the page snappy and
// avoids the BROKER_ACTIVITY feature flag gate (this is lead-capture
// adjacent, surfaced inside the lead_capture subapp).
//
// Mount: routed at /leads/sla under the existing AddOnSubappLayout
// addOn="lead_capture" gate.

import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { Timer, AlertTriangle, Users } from 'lucide-react';
import { functions } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
};

interface BrokerBreach {
  brokerId: string;
  brokerName: string;
  breachCount: number;
  avgBreachMinutes: number;
}

interface SlaSummary {
  unansweredLast24h: number;
  avgSlaBreachMinutes: number;
  totalBreachesInWindow: number;
  lookbackDays: number;
  topBreachBrokers: BrokerBreach[];
}

const LOOKBACK_OPTIONS = [
  { value: 1, label: '24h' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
];

function formatMins(mins: number): string {
  if (mins < 1) return '< 1 min';
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hours < 24) return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours === 0 ? `${days}d` : `${days}d ${remHours}h`;
}

const LeadSlaDashboardPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId;

  const [lookbackDays, setLookbackDays] = useState<number>(7);
  const [data, setData] = useState<SlaSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    const fn = httpsCallable<
      { companyId: string; lookbackDays: number },
      SlaSummary
    >(functions, 'getLeadSlaSummary');
    fn({ companyId, lookbackDays })
      .then((r) => setData(r.data))
      .catch((err) => {
        console.error('[LeadSlaDashboardPage] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, lookbackDays, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <PageHeader
        icon={Timer}
        title="Lead SLA Dashboard"
        subtitle="First-contact SLA breach stats from the escalateUnansweredLeads scheduler. Updated every 5 minutes."
        onRefresh={refresh}
        isRefreshing={loading}
      />

      <div className="flex items-center gap-3 text-sm">
        <label className="text-gray-400">Lookback window</label>
        <select
          value={lookbackDays}
          onChange={(e) => setLookbackDays(Number(e.target.value))}
          className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-white text-sm"
        >
          {LOOKBACK_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-amber-500 p-4">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-gray-400">
              Unanswered (last 24h)
            </div>
            <AlertTriangle className="w-4 h-4 text-amber-400" />
          </div>
          <div className="mt-1 text-3xl font-bold text-amber-400">
            {(data?.unansweredLast24h ?? 0).toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            no firstContactedAt yet — needs follow-up
          </div>
        </div>

        <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-red-500 p-4">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-gray-400">
              Avg breach time
            </div>
            <Timer className="w-4 h-4 text-red-400" />
          </div>
          <div className="mt-1 text-3xl font-bold text-red-400">
            {data ? formatMins(data.avgSlaBreachMinutes) : '—'}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            across {data?.totalBreachesInWindow ?? 0} breaches in {lookbackDays}-day window
          </div>
        </div>

        <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-blue-500 p-4">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wider text-gray-400">
              Brokers with breaches
            </div>
            <Users className="w-4 h-4 text-blue-400" />
          </div>
          <div className="mt-1 text-3xl font-bold text-blue-400">
            {(data?.topBreachBrokers.length ?? 0).toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            top 3 surfaced below
          </div>
        </div>
      </div>

      {/* Top breach brokers */}
      <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-4">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-400" />
          Top breach brokers (last {lookbackDays} day{lookbackDays === 1 ? '' : 's'})
        </h3>
        {loading ? (
          <div className="text-sm text-gray-500">Loading…</div>
        ) : data && data.topBreachBrokers.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-700">
                <th className="text-left py-2 font-medium">Broker</th>
                <th className="text-right py-2 font-medium">Breaches</th>
                <th className="text-right py-2 font-medium">Avg breach</th>
              </tr>
            </thead>
            <tbody>
              {data.topBreachBrokers.map((b) => (
                <tr key={b.brokerId} className="border-b border-gray-800/60">
                  <td className="py-2 text-gray-200">{b.brokerName}</td>
                  <td className="py-2 text-right text-red-300 font-semibold">
                    {b.breachCount}
                  </td>
                  <td className="py-2 text-right text-gray-300 font-mono">
                    {formatMins(b.avgBreachMinutes)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-sm text-gray-400">
            No SLA breaches in the selected window. Either every lead was
            contacted on time, or no leads have entered the SLA window yet.
          </div>
        )}
      </div>

      <div className="text-[11px] text-gray-500">
        SLA window is set per-tenant via{' '}
        <code className="font-mono">leadRoutingRules.slaMinutes</code>.
        Escalation cron runs every 5 minutes ({' '}
        <code className="font-mono">escalateUnansweredLeads</code>).
      </div>
    </div>
  );
};

export default LeadSlaDashboardPage;
